"use client";

import { useEffect, useState, type ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  CheckSquare,
  ExternalLink,
  CircleHelp,
  Flag,
  Gift,
  Link2,
  Loader2,
  Radio,
  Wallet,
  X,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import {
  useClaimMainnetPrivateBetaDiscordRole,
  useIsPrivateBetaClosed,
  useMainnetPrivateBetaEligibility,
  useMainnetPrivateBetaEligibilityCriteria,
} from "@/hooks/useMainnetPrivateBeta";
import { useSocialPayMutations } from "@/hooks/social-pay";
import type { MainnetPrivateBetaEligibility } from "@/types/mainnet-campaign";
import { useMainnetCampaigns } from "@/hooks/useMainnetCampaigns";
import {
  useLinkMainnetWallet,
  useMainnetWalletLinks,
  useSwitchMainnetPrimaryWallet,
} from "@/hooks/useMainnetWalletLinks";
import { useDevnetWalletConnect } from "@/hooks/useDevnetWalletConnect";
import { PulsarService } from "@/services/pulsar.service";
import {
  MAINNET_CAMPAIGN_CONFIG,
  MAINNET_PARTNER_QUESTS,
} from "@/config/campaign";
import {
  CampaignHeader as CampaignHeaderDesign,
  PartnerHero as PartnerHeroDesign,
  CampaignCard as CampaignCardDesign,
  PixelCorners,
  SupportedNetworks,
} from "./campaign-design";
import { useCountdown } from "@/hooks/useCountdown";
import { isLaunchTestPreview } from "@/lib/launch-test";

type CampaignSlug = "reppo";
type CampaignView = "check" | "eligible" | "wave" | "not-eligible";
type LandingState = "check" | "wave-1" | "wave-2" | "wave-3" | "not-eligible";
type CampaignDetailState =
  | "check"
  | "wave-1"
  | "wave-2"
  | "wave-3"
  | "not-eligible";

const MAINNET_REPPO_REWARD_POINTS = "5000";

const campaigns: Record<
  CampaignSlug,
  {
    name: string;
    description: string;
    icon: string;
    iconClassName?: string;
    iconTileClassName?: string;
  }
> = {
  reppo: {
    name: "Reppo",
    description:
      "Network for verified AI training data, powered by prediction markets. On Base.",
    icon: "/assets/campaigns/reppo-logo.png",
    iconClassName: "h-9 w-9",
    iconTileClassName: "bg-white",
  },
};

const onboardingSteps = [
  {
    number: 1,
    title: "Fund Your Wallet",
    description: "Use the wallet connected to your Portal account.",
    icon: Wallet,
  },
  {
    number: 2,
    title: "Open Partner App",
    description:
      "Use the partner app through Spice Flow once the private beta opens.",
    icon: Link2,
  },
  {
    number: 3,
    title: "Complete App Activity",
    description: "Submit at least one transaction in the partner app.",
    icon: Radio,
  },
  {
    number: 4,
    title: "Complete Quests",
    description:
      "Use the checklist to test the integration and submit feedback.",
    icon: CheckSquare,
  },
  {
    number: 5,
    title: "Claim Points",
    description:
      "Once you complete all the quests, claim your points and provide feedback.",
    icon: Gift,
  },
];

const partnerOnboardingSteps = [
  {
    number: 1,
    title: "Fund Your Wallet",
    description: "Use the wallet connected to your Portal account.",
    icon: Wallet,
  },
  {
    number: 2,
    title: "Connect to Partner App",
    description:
      "Find a link below to connect to the partner app and start testing.",
    icon: Link2,
  },
  {
    number: 3,
    title: "Complete Quests",
    description:
      "Complete private beta mainnet quests to test the integration.",
    icon: CheckSquare,
  },
  {
    number: 4,
    title: "Claim Points",
    description:
      "Once you complete all the quests, claim your points and provide feedback.",
    icon: Gift,
  },
];

const reppoQuests = [
  "Lock REPPO",
  "Access datanet",
  "Publish pod",
  "Vote on pod",
  "Provide feedback",
];

const partnerEdgeMarks: Partial<Record<CampaignSlug, string>> = {
  reppo: "/assets/campaigns/reppo-edge-mark.png",
};

const campaignDetailContent: Record<
  CampaignSlug,
  {
    chain: string;
    privateTitle: string;
    heroCopy: ReactNode;
    walletTitle: string;
    walletDescription: string;
    walletNote: string;
    connectTitle: string;
    connectDescription: string;
    welcomeTitle: string;
    welcomeCopy: string;
    appUrl: string;
    questsTitle: string;
    questsIntro: string;
    quests: string[];
    waveDates: Record<2 | 3, string>;
  }
> = {
  reppo: {
    chain: "Base",
    privateTitle: "Reppo Private Beta Mainnet",
    heroCopy: (
      <>
        <span className="block">
          Be the first to test Spice Flow private beta mainnet
        </span>
        <span className="block">
          integration with Reppo on Base. Complete quests,
        </span>
        <span className="block">provide feedback, and earn points.</span>
      </>
    ),
    walletTitle: "Reppo Private Beta Mainnet",
    walletDescription: "Spice Flow x Reppo private beta mainnet.",
    walletNote: "Use your linked secondary wallet in Reppo if it differs from your Portal wallet.",
    connectTitle: "Connect to Reppo",
    connectDescription:
      "Find a link below to connect to Reppo and start testing.",
    welcomeTitle: "Welcome to Reppo",
    welcomeCopy:
      "Be the first to test Spice Flow through Reppo. Complete quests, provide feedback, and earn points.",
    appUrl: "https://reppo.ai/",
    questsTitle: "Reppo Quests",
    questsIntro:
      'Complete the 5 Spice Flow x Reppo quests to earn points and provide feedback. Once you complete all quests, click "Claim points".',
    quests: reppoQuests,
    waveDates: {
      2: "June 19, 2026, 3 PM UTC",
      3: "July 2, 2026, 2:42 PM UTC",
    },
  },
};

const WAVE_START_DATES = {
  1: "2026-06-17T15:00:00.000Z",
  2: "2026-06-19T15:00:00.000Z",
  3: "2026-07-02T14:42:33.914Z",
} as const;

const DiscordIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="#5865F2"
    aria-hidden="true"
  >
    <path d="M20.32 4.37a19.8 19.8 0 0 0-4.89-1.51.08.08 0 0 0-.08.04c-.21.37-.44.86-.61 1.25a18.3 18.3 0 0 0-5.49 0 12.6 12.6 0 0 0-.62-1.25.08.08 0 0 0-.08-.04 19.7 19.7 0 0 0-4.88 1.51.07.07 0 0 0-.03.03C.53 9.05-.32 13.58.1 18.06c0 .02.01.04.03.06a19.9 19.9 0 0 0 5.99 3.03.08.08 0 0 0 .08-.03c.46-.63.87-1.3 1.23-1.99a.08.08 0 0 0-.04-.11 13.1 13.1 0 0 1-1.87-.89.08.08 0 0 1-.01-.13c.13-.1.25-.19.37-.29a.07.07 0 0 1 .08-.01c3.93 1.79 8.18 1.79 12.06 0a.07.07 0 0 1 .08.01c.12.1.25.2.37.29a.08.08 0 0 1-.01.13 12.3 12.3 0 0 1-1.87.89.08.08 0 0 0-.04.11c.36.7.77 1.36 1.23 1.99a.08.08 0 0 0 .08.03 19.8 19.8 0 0 0 6-3.03.08.08 0 0 0 .03-.05c.5-5.18-.84-9.67-3.55-13.66a.06.06 0 0 0-.03-.04ZM8.02 15.33c-1.18 0-2.16-1.08-2.16-2.42s.96-2.42 2.16-2.42c1.21 0 2.18 1.1 2.16 2.42 0 1.34-.96 2.42-2.16 2.42Zm7.98 0c-1.18 0-2.16-1.08-2.16-2.42s.96-2.42 2.16-2.42c1.21 0 2.18 1.1 2.16 2.42 0 1.34-.95 2.42-2.16 2.42Z" />
  </svg>
);

const Wave1Icon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M2 12c2-2 4-2 6 0s4 2 6 0" />
  </svg>
);

const Wave2Icon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M2 9c2-2 4-2 6 0s4 2 6 0" />
    <path d="M2 15c2-2 4-2 6 0s4 2 6 0" />
  </svg>
);

const Wave3Icon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M2 7c2-2 4-2 6 0s4 2 6 0" />
    <path d="M2 12c2-2 4-2 6 0s4 2 6 0" />
    <path d="M2 17c2-2 4-2 6 0s4 2 6 0" />
  </svg>
);

const timelineSteps = [
  {
    number: 1,
    date: "June 17, 2026, 3 PM UTC",
    title: "Wave 1 opens",
    description:
      "Check eligibility, join the private Discord channel, start testing",
    icon: Wave1Icon,
  },
  {
    number: 2,
    date: "June 19, 2026, 3 PM UTC",
    title: "Wave 2 opens",
    description: "More eligible users can join private beta mainnet testing",
    icon: Wave2Icon,
  },
  {
    number: 3,
    date: "July 2, 2026, 2:42 PM UTC",
    title: "Wave 3 opens",
    description: "Final eligible wave opens for mainnet beta access",
    icon: Wave3Icon,
  },
  {
    number: 4,
    date: "July 21st",
    title: "Private beta mainnet closed",
    description: "Private beta mainnet testing ends",
    icon: Flag,
  },
];

const CampaignHeader = ({
  campaign,
  variant = "dark",
  showAction = false,
  statusText = "Opens June 17 at 3 PM UTC",
  isCheckingEligibility = false,
  onCheckEligibility,
  onShowCriteria,
}: {
  campaign?: CampaignSlug;
  variant?: "dark" | "red";
  showAction?: boolean;
  statusText?: string;
  isCheckingEligibility?: boolean;
  onCheckEligibility?: () => void;
  onShowCriteria?: () => void;
}) => {
  const campaignName = campaign ? campaigns[campaign].name : undefined;
  const isClosed = useIsPrivateBetaClosed();
  const badgeLabel = isClosed ? "Completed" : "Upcoming";
  const resolvedStatus = isClosed
    ? "Private beta mainnet has ended"
    : statusText;

  return (
    <CampaignHeaderDesign
      variant={variant}
      campaignName={campaignName ?? "Reppo"}
      badgeLabel={badgeLabel}
      statusText={resolvedStatus}
      title={
        campaignName ? (
          `Spice Flow x ${campaignName}`
        ) : (
          <>
            <span className="block">Spice Flow</span>
            <span className="block md:inline">Private Beta</span>
            <span className="block md:inline md:ml-2">Mainnet</span>
          </>
        )
      }
      description={
        campaignName ? (
          <>
            Be the first to test Spice Flow private beta mainnet integration
            with {campaignName}. Complete quests, provide feedback, and earn
            points.
          </>
        ) : (
          <>
            Join private beta mainnet to test Spice Flow integration with Reppo.
            Complete quests, provide feedback, and earn points!
          </>
        )
      }
    >
      {showAction && (
        <div className="mt-10 flex flex-col items-center gap-5">
          <button
            type="button"
            className="relative w-[calc(100vw-3rem)] min-w-0 max-w-80 border-2 border-dashed border-spicenet-black bg-white px-12 py-5 text-base font-semibold uppercase tracking-[0.03em] text-spicenet-black transition-colors duration-200 hover:bg-spicenet-ash disabled:pointer-events-none disabled:opacity-50 md:w-full md:max-w-none md:min-w-[460px]"
            onClick={onCheckEligibility}
            disabled={isCheckingEligibility}
          >
            <span className="absolute left-1 top-1 h-1.5 w-1.5 bg-spicenet-black" />
            <span className="absolute right-1 top-1 h-1.5 w-1.5 bg-spicenet-black" />
            <span className="absolute bottom-1 left-1 h-1.5 w-1.5 bg-spicenet-black" />
            <span className="absolute bottom-1 right-1 h-1.5 w-1.5 bg-spicenet-black" />
            {isCheckingEligibility ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Checking Eligibility
              </span>
            ) : (
              "Connect to Check Eligibility"
            )}
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 text-sm font-medium text-white transition-colors duration-200 ease-out hover:text-white/75"
            onClick={onShowCriteria}
          >
            <CircleHelp className="h-4 w-4" />
            See eligibility criteria
          </button>
        </div>
      )}
    </CampaignHeaderDesign>
  );
};

const OnboardingSection = ({ campaign }: { campaign: CampaignSlug }) => {
  const content = campaignDetailContent[campaign];
  return (
    <section className="bg-spicenet-black px-5 py-14">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-medium tracking-[-1px] text-white">
            Onboarding
          </h2>
          <p className="mt-2 font-mono text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-gray">
            {content.privateTitle}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="relative bg-white p-7">
            <PixelCorners color="bg-spicenet-gray-light" />
            <div className="mb-4 flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center bg-spicenet-primary/15 text-spicenet-primary">
                <Image
                  src="/assets/exclusive-devnet/discord.png"
                  alt=""
                  width={22}
                  height={22}
                />
              </span>
              <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-dark">
                Discord Access
              </h3>
            </div>
            <p className="mb-5 text-sm font-medium leading-6 text-spicenet-gray">
              Get access to the private beta Discord channel for support,
              updates, and bug reports.
            </p>
            <div className="mb-7 space-y-3 text-sm font-medium text-spicenet-gray">
              <p>
                <span className="mr-2 text-green-500">•</span>
                Spice Seekers role granted when eligible
              </p>
              <p>
                <span className="mr-2 text-green-500">•</span>
                Access to the private #private_beta_mainnet channel
              </p>
            </div>
            <Button variant="primary" className="w-full gap-2">
              <DiscordIcon />
              Link Discord First
            </Button>
          </div>

          <div className="relative bg-white p-7">
            <PixelCorners color="bg-spicenet-gray-light" />
            <div className="mb-4 flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center bg-spicenet-primary/15 text-spicenet-primary">
                <Wallet className="h-5 w-5" />
              </span>
              <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-dark">
                {content.walletTitle}
              </h3>
            </div>
            <p className="mb-5 text-sm font-medium leading-6 text-spicenet-gray">
              {content.walletDescription}
            </p>
            <p className="mb-3 text-sm font-semibold text-green-600">
              Wallet connected
            </p>
            <div className="border border-spicenet-ash bg-white px-4 py-4 text-center font-mono text-sm font-semibold text-spicenet-gray">
              0x40ac...cd88
            </div>
            <p className="mt-4 text-xs font-semibold text-spicenet-gray-light">
              Please use this exact wallet on {campaigns[campaign].name}.
            </p>
          </div>
        </div>

        <div className="mt-6 bg-white p-6 text-sm font-semibold leading-6 text-spicenet-gray">
          <p className="mb-3 text-spicenet-black">Important:</p>
          <p>
            This integration is in private beta mainnet. Please let us know if
            you encounter any issues either on Discord or through the form.
          </p>
          <p>
            Also, since this integration is in mainnet, you will use real funds
            to complete quests. Please use stablecoins. Please test with small
            amounts only. We will not be able to refund lost funds.
          </p>
        </div>

        <div className="relative mt-8 bg-white px-7 py-10">
          <PixelCorners />
          <h3 className="mb-10 text-center text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-dark">
            How to Participate
          </h3>
          <div className="grid gap-8 md:grid-cols-5 md:gap-4">
            {onboardingSteps.map((step, index) => (
              <div key={step.number} className="relative text-center">
                {index < onboardingSteps.length - 1 && (
                  <div className="absolute left-[calc(50%+30px)] top-7 hidden h-px w-[calc(100%-60px)] bg-spicenet-dark md:block">
                    <ArrowRight className="absolute -right-2 -top-2 h-4 w-4 text-spicenet-dark" />
                  </div>
                )}
                <div className="relative mx-auto mb-5 flex h-14 w-14 items-center justify-center bg-spicenet-black">
                  <step.icon className="h-6 w-6 text-white" />
                  <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center bg-spicenet-primary text-xs font-bold text-spicenet-black">
                    {step.number}
                  </span>
                </div>
                <h4 className="mb-2 text-sm font-semibold text-spicenet-dark">
                  {step.title}
                </h4>
                <p className="mx-auto max-w-[150px] text-xs font-medium leading-5 text-spicenet-gray">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WaveCountdownSection = ({ wave = 2 }: { wave?: 2 | 3 }) => {
  const targetDate = WAVE_START_DATES[wave];
  const hasScheduledDate = Boolean(targetDate);
  const { days, hours, minutes, mounted } = useCountdown(targetDate);
  const countdownParts = [
    [mounted ? String(days).padStart(2, "0") : "--", "Days"],
    [mounted ? String(hours).padStart(2, "0") : "--", "Hours"],
    [mounted ? String(minutes).padStart(2, "0") : "--", "Minutes"],
  ];
  const waveStartLabel = targetDate
    ? new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "UTC",
        timeZoneName: "short",
      }).format(new Date(targetDate))
    : "TBD";

  return (
    <section className="bg-white px-5 py-20 text-center md:py-28">
      <Image
        src="/assets/logo/logo.png"
        alt=""
        width={46}
        height={46}
        className="mx-auto mb-8 h-12 w-12 object-contain"
      />
      <h2 className="text-3xl font-medium tracking-[-1.3px] text-spicenet-black md:text-4xl">
        You are eligible for WAVE {wave}
      </h2>
      <p className="mt-5 font-mono text-xs uppercase tracking-[0.12em] text-spicenet-primary">
        <span className="mr-2 inline-flex h-4 w-4 items-center justify-center bg-spicenet-primary text-white">
          !
        </span>
        Private beta opens by wave at 3 PM UTC
      </p>
      {hasScheduledDate ? (
        <div className="mt-12 flex items-start justify-center gap-5 md:gap-8">
          {countdownParts.map(([value, label], index) => (
            <div key={label} className="flex items-start gap-5 md:gap-8">
              {index > 0 && (
                <span className="mt-9 font-mono text-5xl font-bold text-spicenet-primary">
                  :
                </span>
              )}
              <div>
                <div className="relative flex h-28 w-28 items-center justify-center bg-spicenet-black text-6xl font-medium text-white">
                  <PixelCorners />
                  {value}
                </div>
                <p className="mt-4 font-mono text-xs uppercase text-spicenet-gray">
                  {label}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="relative mx-auto mt-12 flex h-28 w-48 items-center justify-center bg-spicenet-black text-6xl font-medium uppercase text-white">
          <PixelCorners />
          TBD
        </div>
      )}
      <p className="mt-12 text-base font-medium text-spicenet-gray">
        Wave {wave} Starting{" "}
        <span className="text-spicenet-black">{waveStartLabel}</span>
      </p>
    </section>
  );
};

const NotEligibleSection = () => (
  <section className="bg-white px-5 py-20 text-center md:py-28">
    <Image
      src="/assets/logo/logo.png"
      alt=""
      width={46}
      height={46}
      className="mx-auto mb-8 h-12 w-12 object-contain"
    />
    <h2 className="text-3xl font-medium tracking-[-1.3px] text-spicenet-black md:text-4xl">
      You are not eligible yet
    </h2>
    <p className="mx-auto mt-5 max-w-xl font-mono text-sm uppercase leading-6 tracking-[0.08em] text-spicenet-gray">
      Eligibility is based on your Spicenet leaderboard rank or qualifying
      Discord roles. Check back as more waves open.
    </p>
    <Link
      href="/campaigns"
      className="mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-primary hover:text-spicenet-primary/80"
    >
      View Mainnet Campaigns
      <ArrowRight className="h-4 w-4" />
    </Link>
  </section>
);

const CampaignCompletedSection = () => (
  <section className="bg-white px-5 py-20 text-center md:py-28">
    <span className="mx-auto mb-8 flex h-14 w-14 items-center justify-center rounded-full bg-spicenet-primary/10 text-spicenet-primary">
      <Flag className="h-7 w-7" />
    </span>
    <h2 className="text-3xl font-medium tracking-[-1.3px] text-spicenet-black md:text-4xl">
      Private beta mainnet complete
    </h2>
    <p className="mx-auto mt-5 max-w-xl font-mono text-sm uppercase leading-6 tracking-[0.08em] text-spicenet-gray">
      This campaign has ended and is no longer accepting participants. Thank you
      to everyone who tested Spice Flow.
    </p>
    <Link
      href="/leaderboard"
      className="mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-primary hover:text-spicenet-primary/80"
    >
      View Leaderboard
      <ArrowRight className="h-4 w-4" />
    </Link>
  </section>
);

const PartnerHero = ({
  campaign,
  onShowCriteria,
}: {
  campaign: CampaignSlug;
  onShowCriteria?: () => void;
}) => {
  const content = campaignDetailContent[campaign];
  const rightEdgeMark = partnerEdgeMarks[campaign];

  return (
    <PartnerHeroDesign
      title={`Spice Flow x ${campaigns[campaign].name}`}
      description={content.heroCopy}
      rightEdgeMark={rightEdgeMark}
    >
      <div className="mt-10 flex flex-col items-center gap-5">
        <button
          type="button"
          className="relative flex w-[calc(100vw-3rem)] min-w-0 max-w-80 items-center justify-center gap-2 bg-[#23a879] px-6 py-5 text-sm font-semibold uppercase tracking-[0.03em] text-white md:w-full md:max-w-none md:min-w-[460px]"
        >
          <PixelCorners color="bg-spicenet-black" />
          <CheckCircle2 className="h-5 w-5" />
          You Are Connected And Eligible
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-2 text-sm font-medium text-white transition-colors duration-200 ease-out hover:text-white/75"
          onClick={onShowCriteria}
        >
          <CircleHelp className="h-4 w-4" />
          See eligibility criteria
        </button>
      </div>
    </PartnerHeroDesign>
  );
};

const PartnerCountdownSection = ({
  campaign,
  wave,
}: {
  campaign: CampaignSlug;
  wave: 2 | 3;
}) => {
  const date = campaignDetailContent[campaign].waveDates[wave];
  const targetDate = WAVE_START_DATES[wave];
  const hasScheduledDate = Boolean(targetDate);
  const { days, hours, minutes, mounted } = useCountdown(targetDate);
  const countdownParts = [
    [mounted ? String(days).padStart(2, "0") : "--", "Days"],
    [mounted ? String(hours).padStart(2, "0") : "--", "Hours"],
    [mounted ? String(minutes).padStart(2, "0") : "--", "Minutes"],
  ];

  return (
    <section className="bg-white px-5 py-16 text-center md:py-24">
      <Image
        src="/assets/logo/logo.png"
        alt=""
        width={46}
        height={46}
        className="mx-auto mb-8 h-12 w-12 object-contain"
      />
      <h2 className="text-3xl font-medium tracking-[-1.3px] text-spicenet-black md:text-4xl">
        You are eligible for WAVE {wave}
      </h2>
      {hasScheduledDate ? (
        <div className="mt-10 flex items-start justify-center gap-4 md:gap-7">
          {countdownParts.map(([value, label], index) => (
            <div key={label} className="flex items-start gap-4 md:gap-7">
              {index > 0 && (
                <span className="mt-8 font-mono text-5xl font-bold text-spicenet-primary">
                  :
                </span>
              )}
              <div>
                <div className="relative flex h-24 w-24 items-center justify-center bg-spicenet-black text-5xl font-medium text-white md:h-28 md:w-28 md:text-6xl">
                  <PixelCorners />
                  {value}
                </div>
                <p className="mt-4 font-mono text-xs uppercase text-spicenet-gray">
                  {label}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="relative mx-auto mt-10 flex h-24 w-40 items-center justify-center bg-spicenet-black text-5xl font-medium uppercase text-white md:h-28 md:w-48 md:text-6xl">
          <PixelCorners />
          TBD
        </div>
      )}
      <p className="mt-10 text-base font-medium text-spicenet-gray">
        Wave {wave} Starting <span className="text-spicenet-black">{date}</span>
      </p>
    </section>
  );
};

// The backend verifies this exact wording; keep it in sync with partner-campaign.service.ts.
export const buildPortalWalletLinkMessage = (
  linkedWalletAddress: string,
  portalWalletAddress: string,
  timestamp: number,
) =>
  `I confirm linking wallet ${linkedWalletAddress} to Spicenet portal account ${portalWalletAddress}.\n\nTimestamp: ${timestamp}\n\nThis signature proves ownership of this wallet address.`;

interface MainnetWalletLinkCardProps {
  address?: string;
  walletTitle: string;
  walletDescription: string;
  walletNote: string;
  emptyWalletMessage?: string;
}

export const MainnetWalletLinkCard = (props: MainnetWalletLinkCardProps) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return (
      <div id="wallet-link" className="relative scroll-mt-8 bg-white p-7">
        <PixelCorners color="bg-spicenet-gray-light" />
        <div className="mb-4 flex items-center gap-4">
          <span className="flex h-10 w-10 items-center justify-center bg-spicenet-primary/15 text-spicenet-primary">
            <Wallet className="h-5 w-5" />
          </span>
          <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-dark">
            {props.walletTitle}
          </h3>
        </div>
        <p className="mb-5 text-sm font-medium leading-6 text-spicenet-gray">
          {props.walletDescription}
        </p>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-spicenet-gray">
            Portal Wallet
          </p>
          <div className="border border-spicenet-ash bg-white px-4 py-4 text-center font-mono text-sm font-semibold text-spicenet-gray">
            {formatAddress(props.address)}
          </div>
        </div>
        <Button variant="primary" className="mt-5 w-full gap-2" disabled>
          <Link2 className="h-4 w-4" />
          Link Another Wallet
        </Button>
        <p className="mt-4 text-xs font-semibold text-spicenet-gray-light">
          {props.walletNote}
        </p>
      </div>
    );
  }

  return <MainnetWalletLinkCardInner {...props} />;
};

const MainnetWalletLinkCardInner = ({
  address,
  walletTitle,
  walletDescription,
  walletNote,
  emptyWalletMessage = "Link a secondary external wallet to be able to connect to Reppo when your Portal wallet cannot be used there.",
}: MainnetWalletLinkCardProps) => {
  const walletLinksQuery = useMainnetWalletLinks();
  const linkWallet = useLinkMainnetWallet();
  const switchPrimaryWallet = useSwitchMainnetPrimaryWallet();
  const {
    status: connectStatus,
    address: connectedAddress,
    error: connectError,
    openConnectModal,
    disconnect,
    signAndLink,
  } = useDevnetWalletConnect();

  const normalizedAddress = address?.toLowerCase();
  const members = walletLinksQuery.data?.members ?? [];
  const linkedWallets = members.filter(
    (member) => member.walletAddress.toLowerCase() !== normalizedAddress,
  );
  const appWallets = linkedWallets;
  const primaryWallet =
    members.find((member) => member.isPrimary)?.walletAddress ??
    walletLinksQuery.data?.canonicalWalletAddress ??
    address;
  const walletPairSummary =
    address && linkedWallets.length > 0
      ? linkedWallets.map((member) => ({
          label: member.namespace.replaceAll("_", " "),
          walletAddress: member.walletAddress,
        }))
      : [];
  const isBusy =
    linkWallet.isPending ||
    switchPrimaryWallet.isPending ||
    connectStatus === "signing";

  const handleSignAndLink = async () => {
    if (!address) {
      toast.error("Connect your portal wallet first.");
      return;
    }

    if (connectedAddress?.toLowerCase() === address.toLowerCase()) {
      toast.error(
        "This is already your portal wallet. Pick a different wallet to link.",
      );
      return;
    }

    const result = await signAndLink(address, buildPortalWalletLinkMessage);
    if (!result || !connectedAddress) return;

    await linkWallet.mutateAsync({
      walletAddress: connectedAddress,
      namespace: "secondary_wallet",
      makePrimary: false,
      message: result.message,
      signature: result.signature,
    });
    await disconnect();
  };

  return (
    <div id="wallet-link" className="relative scroll-mt-8 bg-white p-7">
      <PixelCorners color="bg-spicenet-gray-light" />
      <div className="mb-4 flex items-center gap-4">
        <span className="flex h-10 w-10 items-center justify-center bg-spicenet-primary/15 text-spicenet-primary">
          <Wallet className="h-5 w-5" />
        </span>
        <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-dark">
          {walletTitle}
        </h3>
      </div>
      <p className="mb-5 text-sm font-medium leading-6 text-spicenet-gray">
        {walletDescription}
      </p>

      <div className="space-y-3">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-spicenet-gray">
            Portal Wallet
          </p>
          <div className="border border-spicenet-ash bg-white px-4 py-4 text-center font-mono text-sm font-semibold text-spicenet-gray">
            {formatAddress(address)}
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-spicenet-gray">
            Secondary App Wallet
          </p>
          {walletLinksQuery.isLoading ? (
            <div className="border border-spicenet-ash bg-white px-4 py-4 text-sm font-semibold leading-6 text-spicenet-gray">
              Checking linked wallets...
            </div>
          ) : walletLinksQuery.isError ? (
            <div className="border border-dashed border-spicenet-primary/40 bg-spicenet-primary/5 px-4 py-4 text-sm font-semibold leading-6 text-spicenet-gray">
              Could not load linked wallets. Refresh and try again.
            </div>
          ) : appWallets.length > 0 ? (
            <div className="space-y-2">
              {appWallets.map((member) => (
                <div
                  key={`${member.namespace}-${member.walletAddress}`}
                  className="border border-spicenet-ash bg-spicenet-primary/5 px-4 py-4 text-center"
                >
                  <p className="font-mono text-sm font-semibold text-spicenet-dark">
                    {formatAddress(member.walletAddress)}
                  </p>
                  <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-spicenet-gray">
                    {member.namespace.replaceAll("_", " ")}
                  </p>
                </div>
              ))}
              <p className="text-xs font-semibold text-green-600">
                Use the linked app wallet above when your Portal wallet cannot
                be used in the partner app.
              </p>
            </div>
          ) : (
            <div className="border border-dashed border-spicenet-primary/40 bg-spicenet-primary/5 px-4 py-4 text-sm font-semibold leading-6 text-spicenet-gray">
              {emptyWalletMessage}
            </div>
          )}
        </div>

        {primaryWallet && (
          <p className="text-xs font-semibold text-green-600">
            Primary campaign wallet: {formatAddress(primaryWallet)}
          </p>
        )}

        {walletPairSummary.length > 0 && (
          <div className="space-y-2 border border-spicenet-primary/25 bg-spicenet-primary/5 px-4 py-3 text-xs font-semibold leading-5 text-spicenet-dark">
            <p>
              <span className="text-spicenet-gray">Linked pair: </span>
              <span className="font-mono">{formatAddress(address)}</span>
              <span className="text-spicenet-gray"> linked with </span>
              {walletPairSummary.map((member, index) => (
                <span key={`${member.label}-${member.walletAddress}`}>
                  {index > 0 && <span className="text-spicenet-gray">, </span>}
                  <span className="font-mono">
                    {formatAddress(member.walletAddress)}
                  </span>
                  <span className="ml-1 text-spicenet-gray">
                    ({member.label})
                  </span>
                </span>
              ))}
            </p>
            <p className="text-spicenet-gray">
              Use the linked wallet in the partner app. You can confirm it is
              whitelisted through the partner whitelist check.
            </p>
          </div>
        )}

        {linkedWallets.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-spicenet-gray">
              Linked Wallets
            </p>
            {linkedWallets.map((member) => (
              <div
                key={`${member.namespace}-${member.walletAddress}`}
                className="flex items-center justify-between gap-3 border border-spicenet-ash px-3 py-2"
              >
                <div>
                  <p className="font-mono text-xs font-semibold text-spicenet-dark">
                    {formatAddress(member.walletAddress)}
                  </p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-spicenet-gray">
                    {member.namespace.replaceAll("_", " ")}
                  </p>
                </div>
                {!member.isPrimary && (
                  <button
                    type="button"
                    className="text-xs font-semibold uppercase tracking-[0.08em] text-spicenet-primary disabled:opacity-50"
                    disabled={isBusy}
                    onClick={() =>
                      switchPrimaryWallet.mutate(member.walletAddress)
                    }
                  >
                    Use
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {connectedAddress && (
          <div className="border border-spicenet-primary/30 bg-spicenet-primary/5 px-3 py-3">
            <p className="mb-1 text-xs font-semibold text-spicenet-primary">
              Wallet ready to link
            </p>
            <p className="font-mono text-sm font-semibold text-spicenet-dark">
              {formatAddress(connectedAddress)}
            </p>
          </div>
        )}

        {connectError && (
          <p className="text-xs font-semibold text-spicenet-primary">
            {connectError}
          </p>
        )}
      </div>

      <div className="mt-5">
        {connectedAddress ? (
          <Button
            variant="primary"
            className="w-full gap-2"
            disabled={isBusy}
            onClick={handleSignAndLink}
          >
            {isBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="h-4 w-4" />
            )}
            {connectStatus === "signing" || linkWallet.isPending
              ? "Linking..."
              : "Sign & Link Wallet"}
          </Button>
        ) : (
          <Button
            variant="primary"
            className="w-full gap-2"
            disabled={connectStatus === "connecting"}
            onClick={openConnectModal}
          >
            {connectStatus === "connecting" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="h-4 w-4" />
            )}
            Link Another Wallet
          </Button>
        )}
      </div>

      <p className="mt-4 text-xs font-semibold text-spicenet-gray-light">
        {walletNote}
      </p>
    </div>
  );
};

const PartnerOnboardingSection = ({
  address,
  campaign,
}: {
  address?: string;
  campaign: CampaignSlug;
}) => {
  const content = campaignDetailContent[campaign];
  const { isAuthenticated, login } = useAuth();
  const { linkDiscord } = useSocialPayMutations();
  const eligibilityQuery = useMainnetPrivateBetaEligibility();
  const claimDiscordRole = useClaimMainnetPrivateBetaDiscordRole();
  const isDiscordLinked = Boolean(
    eligibilityQuery.data?.reasons.isDiscordLinked,
  );
  const isEligible = Boolean(eligibilityQuery.data?.isEligible);
  const steps = partnerOnboardingSteps.map((step) =>
    step.number === 2
      ? {
          ...step,
          title: content.connectTitle,
          description: content.connectDescription,
        }
      : step,
  );
  const handleDiscordAction = () => {
    if (!isAuthenticated) {
      login();
      return;
    }

    if (!isDiscordLinked) {
      linkDiscord.mutate();
      return;
    }

    if (isEligible) {
      claimDiscordRole.mutate();
    }
  };
  const discordActionLabel = !isAuthenticated
    ? "Connect Wallet First"
    : !isDiscordLinked
      ? linkDiscord.isPending
        ? "Linking..."
        : "Link Discord First"
      : isEligible
        ? claimDiscordRole.isPending
          ? "Claiming..."
          : `Claim ${eligibilityQuery.data?.claimRoleName ?? "Spice Seekers"} Role`
        : "Discord Linked";

  return (
    <section className="bg-spicenet-black px-5 py-14">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-medium tracking-[-1px] text-white">
            Onboarding
          </h2>
          <p className="mt-2 font-mono text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-gray">
            {content.privateTitle}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="relative bg-white p-7">
            <PixelCorners color="bg-spicenet-gray-light" />
            <div className="mb-4 flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center bg-spicenet-primary/15 text-spicenet-primary">
                <Image
                  src="/assets/exclusive-devnet/discord.png"
                  alt=""
                  width={22}
                  height={22}
                />
              </span>
              <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-dark">
                Discord Access
              </h3>
            </div>
            <p className="mb-5 text-sm font-medium leading-6 text-spicenet-gray">
              Get access to the private beta Discord channel for support,
              updates, and bug reports.
            </p>
            <div className="mb-7 space-y-3 text-sm font-medium text-spicenet-gray">
              <p>
                <span className="mr-2 text-green-500">•</span>
                Spice Seekers role granted when eligible
              </p>
              <p>
                <span className="mr-2 text-green-500">•</span>
                Access to the private #private_beta_mainnet channel
              </p>
            </div>
            <Button
              variant="primary"
              className="w-full gap-2"
              onClick={handleDiscordAction}
              disabled={
                (isDiscordLinked && !isEligible) ||
                linkDiscord.isPending ||
                claimDiscordRole.isPending
              }
            >
              <DiscordIcon />
              {discordActionLabel}
            </Button>
          </div>

          <MainnetWalletLinkCard
            address={address}
            walletTitle={content.walletTitle}
            walletDescription={content.walletDescription}
            walletNote={content.walletNote}
          />
        </div>

        <div className="mt-6 bg-white p-6 text-sm font-semibold leading-6 text-spicenet-gray">
          <p className="mb-3 text-spicenet-black">Important:</p>
          <p>
            This integration is in private beta mainnet. Please let us know if
            you encounter any issues either on Discord or through the form.
          </p>
          <p>
            Also, since this integration is in mainnet, you will use real funds
            to complete quests. Please use stablecoins. Please test with small
            amounts only. We will not be able to refund lost funds.
          </p>
        </div>

        <div className="relative mt-8 bg-white px-7 py-10">
          <PixelCorners />
          <h3 className="mb-10 text-center text-sm font-semibold uppercase tracking-[0.08em] text-spicenet-dark">
            How to Participate
          </h3>
          <div className="grid gap-8 md:grid-cols-4 md:gap-4">
            {steps.map((step, index) => (
              <div key={step.number} className="relative text-center">
                {index < steps.length - 1 && (
                  <div className="absolute left-[calc(50%+30px)] top-7 hidden h-px w-[calc(100%-60px)] bg-spicenet-dark md:block">
                    <ArrowRight className="absolute -right-2 -top-2 h-4 w-4 text-spicenet-dark" />
                  </div>
                )}
                <div className="relative mx-auto mb-5 flex h-14 w-14 items-center justify-center bg-spicenet-black">
                  <step.icon className="h-6 w-6 text-white" />
                  <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center bg-spicenet-primary text-xs font-bold text-spicenet-black">
                    {step.number}
                  </span>
                </div>
                <h4 className="mb-2 text-sm font-semibold text-spicenet-dark">
                  {step.title}
                </h4>
                <p className="mx-auto max-w-[150px] text-xs font-medium leading-5 text-spicenet-gray">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const PartnerWelcomeSection = ({ campaign }: { campaign: CampaignSlug }) => {
  const content = campaignDetailContent[campaign];

  return (
    <section className="bg-white px-5 py-14 text-center md:py-16">
      <h2 className="text-3xl font-medium tracking-[-1px] text-spicenet-black">
        {content.welcomeTitle}
      </h2>
      <p className="mx-auto mt-4 max-w-md text-sm font-semibold leading-6 text-spicenet-gray">
        {content.welcomeCopy}
      </p>
      <a
        href={content.appUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="relative mt-8 inline-flex h-16 items-center justify-center gap-2 bg-spicenet-primary px-16 py-5 text-lg font-medium uppercase tracking-[0.1em] text-spicenet-black transition-colors duration-200 hover:bg-spicenet-primary/90 active:bg-spicenet-primary/80"
      >
        <PixelCorners color="bg-spicenet-black" />
        <span className="relative z-10 flex items-center gap-2 tracking-[0px]">
          Access App And Complete Quests
          <ExternalLink className="h-4 w-4" />
        </span>
      </a>
    </section>
  );
};

const PartnerQuestsSection = ({ campaign }: { campaign: CampaignSlug }) => {
  const content = campaignDetailContent[campaign];
  const { isAuthenticated, login, getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const campaignsData = useMainnetCampaigns();
  const partnerCampaign = campaignsData.campaigns.find(
    (item) => item.partnerId === campaign,
  );
  const claimTier =
    partnerCampaign?.tiers.find((tier) => tier.state === "claimable") ??
    partnerCampaign?.tiers.find((tier) => tier.state === "claimed") ??
    partnerCampaign?.tiers[0];
  const isClaimed = claimTier?.state === "claimed";
  const canClaim =
    claimTier?.state === "claimable" && Boolean(claimTier.claimTaskGuid);
  const feedbackUrl = MAINNET_PARTNER_QUESTS[campaign].feedbackFormUrl;
  const unableToVerifyMessage =
    "Unable to verify, please complete tasks and claim again.";
  const claimRetryMessage =
    "Couldn't confirm your claim yet. Hard refresh the page (Cmd/Ctrl + Shift + R) and try again in a few minutes.";

  const claimMutation = useMutation({
    mutationFn: async () => {
      if (!isAuthenticated) {
        login();
        throw new Error("Connect your wallet to claim points.");
      }

      const accessToken = await getAccessToken();

      if (!accessToken) {
        throw new Error(
          "Authentication is still loading. Please wait a moment and try again.",
        );
      }

      const refreshedCampaign =
        await PulsarService.getMainnetCampaignDetail(campaign, accessToken);
      const refreshedClaimTier =
        refreshedCampaign.tiers.find((tier) => tier.state === "claimable") ??
        refreshedCampaign.tiers.find((tier) => tier.state === "claimed") ??
        refreshedCampaign.tiers[0];

      if (refreshedClaimTier?.state === "claimed") {
        return refreshedClaimTier;
      }

      if (!refreshedClaimTier?.claimTaskGuid) {
        throw new Error(unableToVerifyMessage);
      }

      const claimResult = await PulsarService.claimMainnetTier(
        refreshedClaimTier.claimTaskGuid,
        accessToken,
      );
      if (!claimResult.status) {
        throw new Error(claimResult.error || unableToVerifyMessage);
      }

      return claimResult;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          "mainnet",
          "campaigns",
          MAINNET_CAMPAIGN_CONFIG.id,
          MAINNET_CAMPAIGN_CONFIG.groupId,
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: ["mainnet", "dashboard"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["mainnet", "leaderboard"],
      });
      toast.success("Points claimed.");
    },
    onError: (error) => {
      const backendMessage =
        isAxiosError(error) && typeof error.response?.data?.message === "string"
          ? error.response.data.message
          : error instanceof Error
            ? error.message
            : null;

      // Points already credited: say so plainly instead of asking them to retry.
      if (backendMessage?.toLowerCase().includes("already")) {
        toast.error("You've already claimed these points.");
        return;
      }

      // Raw transport/HTTP errors from the claim endpoint (e.g. "Request failed
      // with status code 400") are transient here and clear on a hard refresh +
      // retry, so guide the user there instead of showing the raw status text.
      if (isAxiosError(error)) {
        toast.error(claimRetryMessage);
        return;
      }

      // Client-side guards (connect wallet, auth still loading) carry actionable copy.
      toast.error(error instanceof Error ? error.message : unableToVerifyMessage);
    },
  });

  return (
    <section className="bg-[#e7e2dc] px-5 py-14">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <h2 className="flex items-center gap-2 text-2xl font-medium tracking-[-0.5px] text-spicenet-black">
            <span className="text-spicenet-primary">♨</span>
            {content.questsTitle}
          </h2>
          <p className="mt-4 text-sm font-semibold leading-6 text-spicenet-gray">
            {content.questsIntro}
          </p>
        </div>

        <div className="bg-spicenet-primary/10 p-5">
          <div className="space-y-2">
            {content.quests.map((quest, index) => {
              const isFeedbackTask = index === content.quests.length - 1;

              return (
                <div
                  key={quest}
                  className="flex min-h-16 items-center justify-between gap-4 bg-white px-6 text-lg font-semibold text-spicenet-black"
                >
                  <div className="flex items-center gap-7">
                    <span className="font-mono text-spicenet-gray">
                      {index + 1}.
                    </span>
                    <span>{quest}</span>
                  </div>
                  {isFeedbackTask && (
                    <Button
                      variant="monochrome"
                      size="sm"
                      className="gap-2"
                      onClick={() =>
                        window.open(
                          feedbackUrl,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Open Link
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-14 text-center">
          <p className="mb-5 text-sm font-semibold text-spicenet-gray">
            {isClaimed
              ? "You have already claimed points for this campaign."
              : canClaim
                ? "One app transaction was detected. You can claim your points now."
                : "Complete at least one app transaction, then return to claim your points."}
          </p>
          <button
            type="button"
            className="relative inline-flex items-center justify-center gap-2 border-2 border-spicenet-primary bg-spicenet-primary/10 px-16 py-5 text-lg font-medium uppercase tracking-[0.03em] text-spicenet-primary transition-colors duration-200 hover:bg-spicenet-primary/20 disabled:pointer-events-none disabled:opacity-50"
            disabled={claimMutation.isPending || isClaimed}
            onClick={() => {
              if (!isAuthenticated) {
                login();
                return;
              }
              claimMutation.mutate();
            }}
          >
            <span className="absolute left-1 top-1 h-1.5 w-1.5 bg-spicenet-primary" />
            <span className="absolute right-1 top-1 h-1.5 w-1.5 bg-spicenet-primary" />
            <span className="absolute bottom-1 left-1 h-1.5 w-1.5 bg-spicenet-primary" />
            <span className="absolute bottom-1 right-1 h-1.5 w-1.5 bg-spicenet-primary" />
            <Gift className="h-4 w-4" />
            {claimMutation.isPending
              ? "Claiming..."
              : isClaimed
                ? `${MAINNET_REPPO_REWARD_POINTS} Points Claimed`
                : `Claim ${MAINNET_REPPO_REWARD_POINTS} Points`}
          </button>
        </div>
      </div>
    </section>
  );
};

export const CampaignDetailPage = ({
  campaign,
  view,
  state,
}: {
  campaign: CampaignSlug;
  view?: CampaignView;
  state?: CampaignDetailState;
}) => {
  const router = useRouter();
  const { address, isAuthenticated, login } = useAuth();
  const eligibilityQuery = useMainnetPrivateBetaEligibility();
  const isClosed = useIsPrivateBetaClosed();
  const [isCriteriaModalOpen, setIsCriteriaModalOpen] = useState(false);

  // Campaign closed: keep the page visible but present it as completed. No
  // eligibility check, onboarding, quests, or wave countdowns.
  if (isClosed) {
    return (
      <div className="overflow-x-hidden bg-white">
        <CampaignHeader
          campaign={campaign}
          onShowCriteria={() => setIsCriteriaModalOpen(true)}
        />
        <CampaignCompletedSection />
        <MainnetEligibilityCriteriaModal
          isOpen={isCriteriaModalOpen}
          onClose={() => setIsCriteriaModalOpen(false)}
          eligibility={eligibilityQuery.data}
        />
      </div>
    );
  }

  const detailState =
    state ??
    (view === "eligible"
      ? "wave-1"
      : view === "wave"
        ? "wave-2"
        : (view ?? "check"));
  const activeView =
    detailState === "wave-1"
      ? "eligible"
      : detailState === "wave-2" || detailState === "wave-3"
        ? "wave"
        : detailState === "not-eligible"
          ? "not-eligible"
          : (view ?? "check");

  if (detailState !== "check" && detailState !== "not-eligible") {
    const waitingWave =
      detailState === "wave-2" || detailState === "wave-3"
        ? detailState === "wave-2"
          ? 2
          : 3
        : null;

    return (
      <div className="overflow-x-hidden bg-white">
        <PartnerHero
          campaign={campaign}
          onShowCriteria={() => setIsCriteriaModalOpen(true)}
        />
        {waitingWave && (
          <PartnerCountdownSection campaign={campaign} wave={waitingWave} />
        )}
        <PartnerOnboardingSection address={address} campaign={campaign} />
        {detailState === "wave-1" && (
          <>
            <PartnerWelcomeSection campaign={campaign} />
            <PartnerQuestsSection campaign={campaign} />
          </>
        )}
        <MainnetEligibilityCriteriaModal
          isOpen={isCriteriaModalOpen}
          onClose={() => setIsCriteriaModalOpen(false)}
          eligibility={eligibilityQuery.data}
        />
      </div>
    );
  }

  const showAction = activeView === "check";
  const showOnboarding = activeView === "eligible";
  const showCountdown = activeView === "wave";
  const showNotEligible = activeView === "not-eligible";
  const statusText = "Opens June 17 at 3 PM UTC";
  const waitingWave = eligibilityQuery.data?.wave === 3 ? 3 : 2;

  const handleCheckEligibility = async () => {
    if (!isAuthenticated) {
      login();
      return;
    }

    const eligibility =
      eligibilityQuery.data ?? (await eligibilityQuery.refetch()).data;

    if (!eligibility) return;

    if (campaign === "reppo") {
      const nextState = getCampaignDetailStateFromEligibility(eligibility);
      router.push(
        nextState === "not-eligible"
          ? "/campaigns?state=not-eligible"
          : `/campaigns/${campaign}?state=${nextState}`,
      );
      return;
    }

    if (eligibility.isEligible) {
      router.push(`/campaigns/${campaign}?view=eligible`);
      return;
    }

    if (eligibility.wave && eligibility.waveStartDate) {
      router.push(`/campaigns/${campaign}?view=wave`);
      return;
    }

    router.push(`/campaigns/${campaign}?view=not-eligible`);
  };

  return (
    <div className="bg-white">
      <CampaignHeader
        campaign={campaign}
        showAction={showAction}
        statusText={statusText}
        isCheckingEligibility={eligibilityQuery.isFetching}
        onCheckEligibility={handleCheckEligibility}
        onShowCriteria={() => setIsCriteriaModalOpen(true)}
      />
      {showOnboarding && <OnboardingSection campaign={campaign} />}
      {showCountdown && <WaveCountdownSection wave={waitingWave} />}
      {showNotEligible && <NotEligibleSection />}
      <MainnetEligibilityCriteriaModal
        isOpen={isCriteriaModalOpen}
        onClose={() => setIsCriteriaModalOpen(false)}
        eligibility={eligibilityQuery.data}
      />
    </div>
  );
};

const getCampaignDetailStateFromEligibility = (
  eligibility: MainnetPrivateBetaEligibility,
): CampaignDetailState => {
  if (isLaunchTestPreview() && eligibility.wave) {
    return "wave-1";
  }

  if (eligibility.isEligible) {
    return "wave-1";
  }

  if (eligibility.wave && eligibility.wave >= 1 && eligibility.wave <= 3) {
    return `wave-${eligibility.wave}` as CampaignDetailState;
  }

  return "not-eligible";
};

const getCampaignCardHref = ({
  campaign,
  landingState,
}: {
  campaign: CampaignSlug;
  landingState?: LandingState;
}) => {
  if (landingState === "not-eligible") {
    return undefined;
  }

  if (landingState) {
    return `/campaigns/${campaign}?state=${landingState}`;
  }

  return `/campaigns/${campaign}`;
};

const CampaignCard = ({
  campaign,
  landingState,
}: {
  campaign: CampaignSlug;
  landingState?: LandingState;
}) => {
  const item = campaigns[campaign];
  const href = getCampaignCardHref({ campaign, landingState });
  return (
    <CampaignCardDesign
      href={href}
      title={item.name}
      description={item.description}
      icon={
        <span
          className={cn(
            "mb-5 flex h-12 w-12 items-center justify-center bg-[#ff6908]",
            item.iconTileClassName,
          )}
        >
          <Image
            src={item.icon}
            alt=""
            width={48}
            height={48}
            className={cn("h-7 w-7 object-contain", item.iconClassName)}
          />
        </span>
      }
    />
  );
};

const formatAddress = (walletAddress?: string) => {
  if (!walletAddress) return "0x40ac...cd88";
  return `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`;
};

const getLandingStateFromEligibility = (
  eligibility?: MainnetPrivateBetaEligibility,
): LandingState | undefined => {
  if (!eligibility) return undefined;

  if (isLaunchTestPreview() && eligibility.wave) {
    return "wave-1";
  }

  if (eligibility.isEligible) {
    return "wave-1";
  }

  if (eligibility.wave && eligibility.wave >= 1 && eligibility.wave <= 3) {
    return `wave-${eligibility.wave}` as LandingState;
  }

  return "not-eligible";
};

const EligibilityCornerMarks = () => (
  <>
    <span className="absolute left-1 top-1 font-mono text-3xl font-bold leading-none text-spicenet-gray sm:-left-2 sm:-top-4">
      +
    </span>
    <span className="absolute right-1 top-1 font-mono text-3xl font-bold leading-none text-spicenet-gray sm:-right-2 sm:-top-4">
      +
    </span>
    <span className="absolute bottom-1 left-1 font-mono text-3xl font-bold leading-none text-spicenet-gray sm:-bottom-4 sm:-left-2">
      +
    </span>
    <span className="absolute bottom-1 right-1 font-mono text-3xl font-bold leading-none text-spicenet-gray sm:-bottom-4 sm:-right-2">
      +
    </span>
  </>
);

const EligibilityStatusSection = ({
  state,
  address,
  isChecking,
  onShowCriteria,
}: {
  state?: LandingState;
  address?: string;
  isChecking?: boolean;
  onShowCriteria?: () => void;
}) => {
  if (!state && !isChecking) return null;

  const isEligible = state?.startsWith("wave") || isChecking;
  const wave = state?.startsWith("wave")
    ? Number(state.replace("wave-", ""))
    : undefined;
  const message = isChecking
    ? "Checking eligibility..."
    : state === "not-eligible"
      ? "You are not eligible for this campaign"
      : wave === 1
        ? "You're Eligible!"
        : `You're eligible for Wave ${wave}`;

  return (
    <section className="bg-white px-5 pb-14 pt-12 text-center">
      <h2 className="text-3xl font-medium tracking-[-1px] text-spicenet-black">
        Eligibility Status
      </h2>
      <p className="mx-auto mt-2 max-w-xs font-mono text-xs font-semibold uppercase leading-5 tracking-[0.08em] text-spicenet-gray md:max-w-none">
        Automatically checked using your connected wallet
      </p>

      <div className="relative mx-auto mt-8 flex min-h-40 w-full max-w-xl items-center justify-center border-2 border-dashed border-spicenet-gray/60 px-5 py-10">
        <EligibilityCornerMarks />
        <div>
          <span
            className={cn(
              "mx-auto flex h-14 w-14 items-center justify-center rounded-full",
              isEligible
                ? "bg-green-100 text-green-500"
                : "bg-red-100 text-spicenet-primary",
            )}
          >
            {isChecking ? (
              <Loader2 className="h-7 w-7 animate-spin" />
            ) : isEligible ? (
              <CheckCircle2 className="h-7 w-7" />
            ) : (
              <XCircle className="h-7 w-7" />
            )}
          </span>
          <p
            className={cn(
              "mt-6 text-base font-semibold",
              isEligible ? "text-green-600" : "text-spicenet-primary",
            )}
          >
            {message}
          </p>
          <p className="mt-4 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-spicenet-gray">
            Wallet: {formatAddress(address)}
          </p>
        </div>
      </div>

      <button
        type="button"
        className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-spicenet-primary transition-colors duration-200 ease-out hover:text-spicenet-primary/75"
        onClick={onShowCriteria}
      >
        <CircleHelp className="h-4 w-4" />
        See eligibility criteria
      </button>
    </section>
  );
};

const CriteriaResult = ({
  passed,
  children,
}: {
  passed?: boolean;
  children: ReactNode;
}) => (
  <li className="flex items-start gap-4">
    <span
      className={cn(
        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
        passed
          ? "border-green-500 text-green-500"
          : "border-spicenet-primary text-spicenet-primary",
      )}
    >
      {passed ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <X className="h-3.5 w-3.5" />
      )}
    </span>
    <span className="text-sm leading-6 text-spicenet-gray">{children}</span>
  </li>
);

const HowToStep = ({
  number,
  children,
}: {
  number: number;
  children: ReactNode;
}) => (
  <li className="flex items-start gap-4">
    <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-spicenet-black font-mono text-xs font-bold text-white">
      {number}
    </span>
    <span className="text-sm leading-6 text-spicenet-gray">{children}</span>
  </li>
);

const MainnetEligibilityCriteriaModal = ({
  isOpen,
  onClose,
  eligibility,
}: {
  isOpen: boolean;
  onClose: () => void;
  eligibility?: MainnetPrivateBetaEligibility;
}) => {
  const { isAuthenticated, login } = useAuth();
  const { linkDiscord } = useSocialPayMutations();
  const { data: criteria } = useMainnetPrivateBetaEligibilityCriteria();
  const claimDiscordRole = useClaimMainnetPrivateBetaDiscordRole();
  const discordNoticeRoles =
    criteria?.eligibleDiscordRoles?.join(", ") ||
    "Chilli, Spice Chads, Spice Kings, Spice Gods, or Spice Seekers";
  const criteriaRoleText = discordNoticeRoles;
  const leaderboardLimit =
    criteria?.waves?.reduce(
      (max, wave) => Math.max(max, wave.leaderboardRankLimit),
      30000,
    ) ?? 30000;
  const hasRank = eligibility?.reasons.hasLeaderboardRank;
  const hasDiscordRole = eligibility?.reasons.hasDiscordRole;
  const isDiscordLinked = eligibility?.reasons.isDiscordLinked === true;
  const showDiscordNotice = !isDiscordLinked;
  const showClaimRole = Boolean(eligibility?.isEligible && isDiscordLinked);
  const handleLinkDiscord = () => {
    if (!isAuthenticated) {
      login();
      return;
    }

    linkDiscord.mutate();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-5 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mainnet-eligibility-title"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Close eligibility criteria"
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-[88vh] w-full max-w-xl flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between px-6 pb-7 pt-6">
          <h2
            id="mainnet-eligibility-title"
            className="text-xl font-medium tracking-[-0.5px] text-spicenet-black"
          >
            Who&apos;s Eligible?
          </h2>
          <button
            type="button"
            className="p-2 text-spicenet-black transition-colors duration-200 hover:text-spicenet-primary"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-8 overflow-y-auto px-6 pb-7">
          <p className="max-w-lg text-sm leading-6 text-spicenet-gray">
            The Spice Flow private beta mainnet campaigns are open to a select
            number of our most dedicated community members. If you&apos;re not
            eligible in this round, stay tuned as we open up access in the
            coming weeks.
          </p>

          {showDiscordNotice && (
            <div className="border border-[#ff9d19] bg-[#fff8e5] p-4">
              <div className="flex items-start gap-4">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 fill-[#a84600] text-[#a84600]" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#a84600]">
                    Discord Not Linked
                  </p>
                  <p className="mt-2 text-sm leading-6 text-[#c24f00]">
                    {discordNoticeRoles} roles.
                  </p>
                  <button
                    type="button"
                    className="mt-5 inline-flex items-center gap-2 bg-spicenet-black px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-spicenet-dark disabled:opacity-60"
                    onClick={handleLinkDiscord}
                    disabled={linkDiscord.isPending}
                  >
                    <DiscordIcon />
                    {linkDiscord.isPending ? "Linking..." : "Link Discord"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showClaimRole && (
            <div className="border border-green-500/30 bg-green-50 p-4">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-green-700">
                    Eligible Discord Linked
                  </p>
                  <p className="mt-2 text-sm leading-6 text-green-800">
                    Claim the {eligibility?.claimRoleName ?? "Spice Seekers"}{" "}
                    role to access the private beta channel.
                  </p>
                  <button
                    type="button"
                    className="mt-5 inline-flex items-center gap-2 bg-spicenet-black px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-spicenet-dark disabled:opacity-60"
                    onClick={() => claimDiscordRole.mutate()}
                    disabled={claimDiscordRole.isPending}
                  >
                    <DiscordIcon />
                    {claimDiscordRole.isPending
                      ? "Claiming..."
                      : `Claim ${eligibility?.claimRoleName ?? "Spice Seekers"} Role`}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div>
            <h3 className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-spicenet-gray-light">
              Eligibility Criteria
            </h3>
            <ul className="space-y-4">
              <CriteriaResult passed={hasRank}>
                You are among the first {Math.round(leaderboardLimit / 1000)}k
                portal users on the leaderboard.
              </CriteriaResult>
              <CriteriaResult passed={hasDiscordRole}>
                You hold {criteriaRoleText} roles in the Spicenet Discord
              </CriteriaResult>
            </ul>
          </div>

          <div>
            <h3 className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-spicenet-gray-light">
              How To Check Eligibility
            </h3>
            <ol className="space-y-4">
              <HowToStep number={1}>
                Connect with the Portal wallet that&apos;s associated with your
                account
              </HowToStep>
              <HowToStep number={2}>
                If you&apos;re checking eligibility via a Discord role, make
                sure your Discord is connected to your Portal account/wallet
              </HowToStep>
            </ol>
          </div>
        </div>

        <button
          type="button"
          className="relative bg-spicenet-black py-5 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:bg-spicenet-dark"
          onClick={onClose}
        >
          <PixelCorners color="bg-spicenet-gray" />
          Got It
        </button>
      </div>
    </div>
  );
};

export const CampaignIndexPage = ({
  stateOverride,
}: {
  stateOverride?: LandingState;
}) => {
  const { address, isAuthenticated, login } = useAuth();
  const eligibilityQuery = useMainnetPrivateBetaEligibility();
  const isClosed = useIsPrivateBetaClosed();
  const [isCriteriaModalOpen, setIsCriteriaModalOpen] = useState(false);
  const derivedState = getLandingStateFromEligibility(eligibilityQuery.data);
  // When closed, ignore eligibility-derived states so the page reads as
  // completed and the campaign card stays visible and clickable.
  const landingState = isClosed
    ? undefined
    : (stateOverride ?? (isAuthenticated ? derivedState : undefined));
  const isCheckingStatus =
    !isClosed &&
    !stateOverride &&
    isAuthenticated &&
    eligibilityQuery.isFetching &&
    !eligibilityQuery.data;

  const handleCheckEligibility = async () => {
    if (!isAuthenticated) {
      login();
      return;
    }

    await eligibilityQuery.refetch();
  };

  return (
    <div className="overflow-x-hidden bg-white">
      <CampaignHeader
        variant="red"
        showAction={!isClosed && !landingState && !isCheckingStatus}
        isCheckingEligibility={eligibilityQuery.isFetching}
        onCheckEligibility={handleCheckEligibility}
        onShowCriteria={() => setIsCriteriaModalOpen(true)}
      />

      {isClosed ? (
        <CampaignCompletedSection />
      ) : (
        <EligibilityStatusSection
          state={landingState}
          address={address}
          isChecking={isCheckingStatus}
          onShowCriteria={() => setIsCriteriaModalOpen(true)}
        />
      )}

      <section className="px-5 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="mb-10 text-center text-3xl font-medium tracking-[-1px] text-spicenet-black">
            Available Campaigns
          </h2>

          <div className="grid gap-6 md:grid-cols-2">
            <CampaignCard campaign="reppo" landingState={landingState} />
          </div>

          <div className="mt-12 border border-spicenet-primary/40 bg-spicenet-primary/5 p-6">
            <div className="flex gap-5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-spicenet-primary text-white">
                <ArrowRight className="h-5 w-5" />
              </span>
              <div className="space-y-5 font-mono text-sm font-semibold leading-6 text-spicenet-dark">
                <p>
                  {isClosed
                    ? "The Spice Flow private beta mainnet has wrapped up. Thank you to our core community for helping test the integration."
                    : "Spice Flow integrations open for private beta mainnet on June 17 at 3 PM UTC. We invite our core community to get ready to test."}
                </p>
                <p>
                  Your participation is a key factor in making sure Spice Flow
                  is ready for broader mainnet access. Thank you!
                </p>
                <p>
                  With Spice Flow, you can now access and use Reppo directly
                  from supported networks.
                </p>
              </div>
            </div>
          </div>

          <SupportedNetworks networks={["Citrea", "Arbitrum", "Base"]} />
        </div>
      </section>

      <section className="bg-spicenet-black px-5 py-16 text-white">
        <div className="mx-auto max-w-5xl text-center">
          <h2 className="text-3xl font-medium tracking-[-1px]">Timeline</h2>
          <p className="mx-auto mt-5 max-w-2xl font-mono text-sm font-semibold leading-6 text-spicenet-gray">
            These are the first Spice Flow integrations to open in mainnet and
            your chance to be the first to test our product.
          </p>

          <p className="mt-16 font-mono text-xs text-spicenet-gray">
            Private Beta Mainnet
          </p>
          <div className="mt-10 grid gap-10 md:grid-cols-4">
            {timelineSteps.map((step, index) => (
              <div key={step.number} className="relative text-center">
                {index < timelineSteps.length - 1 && (
                  <span className="absolute left-[calc(50%+40px)] top-7 hidden text-sm text-spicenet-gray md:block">
                    →
                  </span>
                )}
                <div className="relative mx-auto mb-5 flex h-14 w-14 items-center justify-center bg-[#3a3836]">
                  <step.icon className="h-6 w-6 text-white" />
                  <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center bg-spicenet-primary text-xs font-bold text-spicenet-black">
                    {step.number}
                  </span>
                </div>
                <p className="font-mono text-xs font-semibold uppercase text-spicenet-primary">
                  {step.date}
                </p>
                <h3 className="mt-3 text-sm font-semibold">{step.title}</h3>
                <p className="mx-auto mt-4 max-w-[230px] text-xs font-semibold leading-5 text-spicenet-gray">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MainnetEligibilityCriteriaModal
        isOpen={isCriteriaModalOpen}
        onClose={() => setIsCriteriaModalOpen(false)}
        eligibility={eligibilityQuery.data}
      />
    </div>
  );
};

export const isCampaignSlug = (slug: string): slug is CampaignSlug => {
  return slug === "reppo";
};

export const isCampaignView = (view: string | null): view is CampaignView => {
  return (
    view === "check" ||
    view === "eligible" ||
    view === "wave" ||
    view === "not-eligible"
  );
};

export const isLandingState = (state: string | null): state is LandingState => {
  return (
    state === "check" ||
    state === "wave-1" ||
    state === "wave-2" ||
    state === "wave-3" ||
    state === "not-eligible"
  );
};

export const isCampaignDetailState = (
  state: string | null,
): state is CampaignDetailState => {
  return (
    state === "check" ||
    state === "wave-1" ||
    state === "wave-2" ||
    state === "wave-3" ||
    state === "not-eligible"
  );
};
